import React, { useState } from "react";
import API from "../../config/api";
import { useAuth } from "../../hooks/useAuth";
import KnowledgeBasePage from "../KnowledgeBase";
import CampaignPage from "../Campaign";
import CallLogsPage from "../CallLogs";

export default function Dashboard() {
  const { user, logout, getToken } = useAuth();
  const [activeTab, setActiveTab] = useState("kb");
  const [profile, setProfile] = useState({ company_name: user?.company_name || "", twilio_number: user?.twilio_number || "", twilio_sid: user?.twilio_sid || "", twilio_token: user?.twilio_token || "" });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  const saveProfile = async () => {
    setSaving(true); setSaveMsg("");
    try {
      const res = await fetch(API.smeProfile, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(profile),
      });
      const data = await res.json();
      if (data.success) setSaveMsg("✅ Profile saved");
      else setSaveMsg("❌ Failed to save");
    } catch (e) { setSaveMsg("❌ " + e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={S.shell}>
      {/* Sidebar */}
      <div style={S.sidebar}>
        <div style={S.brand}>
          <span style={{ color: "#FF9900", fontWeight: "bold" }}>DigiTran</span>
          <span style={{ color: "#fff", marginLeft: 6, fontSize: 13 }}>Voice AI</span>
        </div>

        <div style={S.userInfo}>
          <div style={S.avatar}>{(user?.name || "U")[0].toUpperCase()}</div>
          <div>
            <div style={{ color: "#e6edf3", fontSize: 13, fontWeight: "bold" }}>{user?.name}</div>
            <div style={{ color: "#8b949e", fontSize: 11 }}>{user?.email}</div>
          </div>
        </div>

        {[
          ["kb", "📚 Knowledge Base"],
          ["calls", "📞 Call Logs"],
          ["campaign", "📣 Campaign"],
          ["profile", "⚙️ Profile"],
        ].map(([key, label]) => (
          <button key={key} style={{ ...S.navBtn, ...(activeTab === key ? S.navActive : {}) }}
            onClick={() => setActiveTab(key)}>
            {label}
          </button>
        ))}

        <button style={S.logoutBtn} onClick={logout}>← Logout</button>
      </div>

      {/* Main content */}
      <div style={S.main}>
        {activeTab === "kb" && <KnowledgeBasePage />}
        {activeTab === "calls" && <CallLogsPage />}
        {activeTab === "campaign" && <CampaignPage />}
        {activeTab === "profile" && (
          <div>
            <h2 style={{ color: "#e6edf3", marginBottom: 6 }}>⚙️ Profile Settings</h2>
            <p style={{ color: "#8b949e", fontSize: 13, marginBottom: 24 }}>
              Set your company name and Twilio virtual number. Incoming calls to your Twilio number will use your knowledge base.
            </p>

            <div style={S.profileCard}>
              <label style={S.label}>Company Name</label>
              <input style={S.input} placeholder="DigiTran Solutions Pvt Ltd"
                value={profile.company_name} onChange={e => setProfile({ ...profile, company_name: e.target.value })} />

              <label style={S.label}>Your Twilio Virtual Number</label>
              <input style={S.input} placeholder="+16414496885"
                value={profile.twilio_number} onChange={e => setProfile({ ...profile, twilio_number: e.target.value })} />
              <p style={{ color: "#8b949e", fontSize: 11, marginBottom: 16 }}>
                When someone calls this number, the AI will answer using your knowledge base.
              </p>

              <label style={S.label}>Twilio Account SID</label>
              <input style={S.input} placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                value={profile.twilio_sid} onChange={e => setProfile({ ...profile, twilio_sid: e.target.value })} />

              <label style={S.label}>Twilio Auth Token</label>
              <input style={{ ...S.input, fontFamily: "monospace" }} placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" type="password"
                value={profile.twilio_token} onChange={e => setProfile({ ...profile, twilio_token: e.target.value })} />

              <button style={S.saveBtn} onClick={saveProfile} disabled={saving}>
                {saving ? "Saving..." : "Save Profile"}
              </button>
              {saveMsg && <p style={{ color: saveMsg.startsWith("✅") ? "#3fb950" : "#f85149", fontSize: 13, marginTop: 8 }}>{saveMsg}</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const S = {
  shell: { display: "flex", minHeight: "100vh", background: "#0d1117" },
  sidebar: { width: 220, background: "#161b22", borderRight: "1px solid #30363d", padding: "20px 16px", display: "flex", flexDirection: "column", gap: 4 },
  brand: { fontSize: 18, marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid #30363d" },
  userInfo: { display: "flex", alignItems: "center", gap: 10, marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid #30363d" },
  avatar: { width: 36, height: 36, borderRadius: "50%", background: "#FF9900", display: "flex", alignItems: "center", justifyContent: "center", color: "#000", fontWeight: "bold", fontSize: 16, flexShrink: 0 },
  navBtn: { padding: "10px 12px", background: "transparent", border: "none", borderRadius: 8, color: "#8b949e", cursor: "pointer", textAlign: "left", fontSize: 13 },
  navActive: { background: "#1f6feb22", color: "#58a6ff" },
  logoutBtn: { marginTop: "auto", padding: "10px 12px", background: "transparent", border: "none", color: "#8b949e", cursor: "pointer", textAlign: "left", fontSize: 13 },
  main: { flex: 1, padding: "32px 40px", overflowY: "auto" },
  profileCard: { background: "#161b22", border: "1px solid #30363d", borderRadius: 10, padding: 24, maxWidth: 480 },
  label: { display: "block", color: "#8b949e", fontSize: 12, marginBottom: 6 },
  input: { width: "100%", padding: "10px 14px", marginBottom: 16, background: "#0d1117", border: "1px solid #30363d", borderRadius: 8, color: "#fff", fontSize: 14, boxSizing: "border-box" },
  saveBtn: { padding: "10px 24px", background: "#238636", border: "none", borderRadius: 8, color: "#fff", fontWeight: "bold", cursor: "pointer", fontSize: 14 },
};
